import { useCallback, useMemo, useState } from "react";
import { useLingui } from "@lingui/react";
import { DropdownOutlined } from "src/atoms/DropDown";
import { FilterCountBadge } from "src/atoms/FilterCountBadge";
import {
  FilterContentWrapper,
  ScrollWrapper,
} from "src/organisms/ProductList/components/Filters/DesktopFilter.styled";
import { ResetFilters } from "src/organisms/ProductList/components/ResetFilters";
import { ColorFilterCheckboxOzon } from "../ColorFilterCheckbox";
import { Box, Flex } from "@otrium/core";
import { Checkbox } from "src/atoms/Checkbox";
import { InputTextSearch } from "../InputTextSearch";
import { debounce } from "src/utils";
import { Radio } from "src/atoms/Radio";
import { useSegment } from "src/hooks/useSegment";
import { shopTypeTranslation } from "src/translations/shopTypeTranslation";
import { FilterType } from "src/types/filters";
import { useFacetContext } from "src/organisms/ProductList/components/Facets/facet.context";
import type {
  ListingFacet,
  ListingFacetData,
} from "src/types/adapters/listing.type";
import {
  isListingFacetBrandData,
  isListingFacetCategoryData,
  isListingFacetColorData,
} from "src/utils/type-guards";
import { ShopType } from "src/utils/genderLocale/genderLocale";

const multicolorIconLink =
  "https://colors-icons.s3.eu-central-1.amazonaws.com/multicoloured.png";

type FilterProps = {
  placeholder: string;
  type: FilterType;
  facet: ListingFacet;
  isMultiColumn?: boolean;
} & (
  | { showSearch: true; searchPlaceholder: string }
  | { showSearch?: false; searchPlaceholder?: string }
);

const GENDER_ORDER: Record<string, number> = {
  [ShopType.Women]: 0,
  [ShopType.Men]: 1,
  [ShopType.Kids]: 2,
};

function compareByLabel(a: ListingFacetData, b: ListingFacetData) {
  const aLabel = (a.label.split("||")[1] || a.label).toLocaleLowerCase();
  const bLabel = (b.label.split("||")[1] || b.label).toLocaleLowerCase();

  return aLabel.localeCompare(bLabel);
}

function compareByCount(a: ListingFacetData, b: ListingFacetData) {
  return b.count - a.count;
}

function compareByGenderOrder(a: ListingFacetData, b: ListingFacetData) {
  const aOrder = GENDER_ORDER[a.value.toLocaleLowerCase()] ?? Infinity;
  const bOrder = GENDER_ORDER[b.value.toLocaleLowerCase()] ?? Infinity;

  return aOrder - bOrder;
}

function Filter({
  placeholder,
  type,
  facet,
  showSearch,
  searchPlaceholder,
  isMultiColumn,
}: FilterProps) {
  const { i18n } = useLingui();
  const [isOpen, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const { segmentProductListFilterOpened } = useSegment();
  const { refine, refineSingle, clearKey } = useFacetContext();

  const items = useMemo<ListingFacetData[]>(() => {
    if (facet.type === "range") {
      return [];
    }

    const sorter =
      facet.key === "brands"
        ? compareByCount
        : facet.key === "genders"
        ? compareByGenderOrder
        : compareByLabel;

    const data: ListingFacetData[] = facet.data;
    const filtered = searchQuery
      ? data.filter((option) =>
          option.label.toLowerCase().includes(searchQuery.toLowerCase())
        )
      : data;

    return [...filtered].sort(sorter);
  }, [facet.type, facet.key, facet.data, searchQuery]);

  const activeCount = useMemo(() => {
    if (facet.type === "range") {
      return 0;
    }

    return facet.data.filter((option) => option.selected).length;
  }, [facet]);

  const canRefine = facet.type !== "range" && facet.data.length > 0;

  const toggleOpen = debounce(() => {
    setOpen((prevState) => !prevState);

    if (!isOpen) {
      setSearchQuery("");
      void segmentProductListFilterOpened(facet.key);
    }
  }, 250);

  const closeDropdown = useCallback(() => {
    setOpen(false);
  }, []);

  const handleRefine = useCallback(
    (value: string) => {
      if (facet.type === "single") {
        refineSingle(facet.key, value);
        return;
      }

      refine(facet.key, value);
    },
    [facet.key, facet.type, refine, refineSingle]
  );

  const handleResetFilters = useCallback(() => {
    clearKey(facet.key);
    setOpen(false);
  }, [facet.key, clearKey]);

  if (!canRefine) {
    return null;
  }

  return (
    <DropdownOutlined
      opened={isOpen}
      disabled={!canRefine}
      hasActiveFilters={activeCount > 0}
      onClose={closeDropdown}
    >
      <DropdownOutlined.Button onClick={canRefine ? toggleOpen : undefined}>
        <span>
          {placeholder}
          <FilterCountBadge count={activeCount} />
        </span>
      </DropdownOutlined.Button>
      {isOpen ? (
        <DropdownOutlined.Items>
          <Flex flexDirection="column" maxHeight="inherit">
            {showSearch ? (
              <Box paddingBlockEnd={3}>
                <InputTextSearch
                  placeholder={searchPlaceholder}
                  handleChange={(event) =>
                    setSearchQuery(event.currentTarget.value)
                  }
                  onClearSearch={() => setSearchQuery("")}
                />
              </Box>
            ) : null}
            <ScrollWrapper>
              <FilterContentWrapper
                as={facet.key === "colors" ? "div" : "ul"}
                isMultiColumn={isMultiColumn && items.length > 1}
                count={Math.round(items.length / 2)}
              >
                {items.map((option) => {
                  const genderDescriptor =
                    facet.key === "genders"
                      ? shopTypeTranslation[
                          String(option.value).toLocaleLowerCase()
                        ]
                      : undefined;
                  const localizedLabel =
                    facet.key === "genders" && genderDescriptor
                      ? i18n._(genderDescriptor)
                      : option.label;

                  if (type === "color" && isListingFacetColorData(option)) {
                    return (
                      <ColorFilterCheckboxOzon
                        key={option.value}
                        id={option.id}
                        checked={option.selected}
                        onChange={() => handleRefine(option.value)}
                        icon={
                          option.icon ||
                          (option.hex ? undefined : multicolorIconLink)
                        }
                        name={localizedLabel}
                        hex={option.hex}
                      />
                    );
                  }

                  if (type === "select") {
                    return (
                      <Box as="li" key={`${facet.key}-${option.value}`}>
                        <Radio
                          id={option.value}
                          name={facet.key}
                          label={option.label}
                          value={option.value}
                          checked={option.selected}
                          onChange={() => handleRefine(option.value)}
                        />
                      </Box>
                    );
                  }

                  return (
                    <Box as="li" key={`${facet.key}-${option.value}`}>
                      <Checkbox
                        id={
                          isListingFacetBrandData(option) ||
                          isListingFacetCategoryData(option)
                            ? option.id
                            : undefined
                        }
                        checked={option.selected}
                        onChange={() => handleRefine(option.value)}
                      >
                        {localizedLabel}
                      </Checkbox>
                    </Box>
                  );
                })}
              </FilterContentWrapper>
            </ScrollWrapper>

            <ResetFilters
              activeCount={activeCount}
              onClear={handleResetFilters}
            />
          </Flex>
        </DropdownOutlined.Items>
      ) : null}
    </DropdownOutlined>
  );
}

export { Filter };
